import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetRentalByIdQuery, useCreateRentalMutation } from '../../../store/api/rentalApi';
import { useGetCustomersQuery } from '../../../store/api/salesApi';
import { useGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const RentalForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isView = Boolean(id);

  const [createRental, { isLoading: isCreating }] = useCreateRentalMutation();
  const { data: contractData, isLoading: isLoadingContract } = useGetRentalByIdQuery(id, { skip: !isView });
  const { data: customersData } = useGetCustomersQuery({ limit: 1000 });
  const { data: productsData } = useGetProductsQuery({ limit: 1000, isRentable: true });
  const { data: company } = useGetCurrentCompanyQuery();

  const currency = company?.currency || 'USD';
  const currencyDecimals = company?.currencyDecimals ?? 2;

  const customersList = customersData?.data || (Array.isArray(customersData) ? customersData : (customersData?.customers || []));
  const productsList = productsData?.data || (Array.isArray(productsData) ? productsData : (productsData?.products || []));

  const [formData, setFormData] = useState({
    customerId: '',
    startDate: null,
    endDate: null,
    depositAmount: '',
    notes: '',
  });

  const [items, setItems] = useState([
    { productId: '', quantity: 1, unitPrice: 0, periodType: 'day', serialNumbers: [] }
  ]);

  useEffect(() => {
    if (contractData && isView) {
      setFormData({
        customerId: contractData.customerId,
        startDate: contractData.startDate ? new Date(contractData.startDate) : null,
        endDate: contractData.endDate ? new Date(contractData.endDate) : null,
        depositAmount: contractData.depositAmount,
        notes: contractData.notes || '',
      });
      setItems(contractData.items.map(it => ({
        productId: it.productId,
        quantity: it.quantity,
        unitPrice: it.unitPrice,
        periodType: it.periodType,
        serialNumbers: it.serialNumbers || [],
      })));
    }
  }, [contractData, isView]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Auto-fill price based on product
    if (field === 'productId') {
      const prod = productsData?.products?.find(p => p.id === value);
      if (prod) {
        newItems[index].unitPrice = prod.rentalRate || 0;
        newItems[index].periodType = prod.rentalPeriodType || 'day';
      }
    }
    
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { productId: '', quantity: 1, unitPrice: 0, periodType: 'day', serialNumbers: [] }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        startDate: formData.startDate ? formData.startDate.toISOString() : null,
        endDate: formData.endDate ? formData.endDate.toISOString() : null,
        items: items.filter(it => it.productId),
      };
      await createRental(payload).unwrap();
      navigate('/rentals');
    } catch (error) {
      console.error('Failed to create rental:', error);
      alert('Failed to create rental contract');
    }
  };

  const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0)), 0);
  const grandTotal = subtotal - (parseFloat(formData.depositAmount) || 0);

  if (isView && isLoadingContract) return <Typography>Loading...</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
        {isView ? `View Rental Contract` : 'New Rental Contract'}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Contract Details</Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Customer"
                value={formData.customerId}
                onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                required
                disabled={isView}
              >
                <MenuItem value="" disabled>Select Customer</MenuItem>
                {customersList.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Security Deposit"
                value={formData.depositAmount}
                onChange={(e) => setFormData({ ...formData, depositAmount: e.target.value })}
                disabled={isView}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate}
                  onChange={(newValue) => setFormData({ ...formData, startDate: newValue })}
                  disabled={isView}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={(newValue) => setFormData({ ...formData, endDate: newValue })}
                  disabled={isView}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                disabled={isView}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Rental Items</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Total</TableCell>
                  {!isView && <TableCell align="center">Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        value={item.productId}
                        onChange={(e) => handleItemChange(index, 'productId', e.target.value)}
                        disabled={isView}
                        required
                      >
                        <MenuItem value="" disabled>Select Product</MenuItem>
                        {productsList.map(p => (
                          <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        disabled={isView}
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                        disabled={isView}
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={item.periodType}
                        onChange={(e) => handleItemChange(index, 'periodType', e.target.value)}
                        disabled={isView}
                      >
                        <MenuItem value="day">Day</MenuItem>
                        <MenuItem value="week">Week</MenuItem>
                        <MenuItem value="month">Month</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>
                      {new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: currencyDecimals }).format(Number(parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0)) || 0)}
                    </TableCell>
                    {!isView && (
                      <TableCell align="center">
                        <IconButton color="error" onClick={() => removeItem(index)} disabled={items.length === 1}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!isView && (
            <Button startIcon={<AddIcon />} onClick={addItem} sx={{ mt: 2 }}>
              Add Item
            </Button>
          )}
          
          <Box sx={{ mt: 3, textAlign: 'right' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">Subtotal:</Typography>
              <Typography variant="h6" fontWeight="bold">
                {new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: currencyDecimals }).format(Number(subtotal) || 0)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">Deposit Amount:</Typography>
              <Typography variant="h6" fontWeight="bold">
                {new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: currencyDecimals }).format(Number(formData.depositAmount) || 0)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h5" fontWeight="bold">Grand Total:</Typography>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: currencyDecimals }).format(Number(grandTotal) || 0)}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isView && (
            <Button type="submit" variant="contained" color="primary" disabled={isCreating}>
              Create Contract
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate('/rentals')}>
            {isView ? 'Back' : 'Cancel'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default RentalForm;
